"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable, type Column } from "./DataTable";
import { CSVExportButton, PrintButton, type PrintData } from "./PrintExport";
import { useERP, formatBDT, formatNum, type ERPState } from "@/lib/erp/store";
import { Calendar, Building2, FileBarChart, Hash } from "lucide-react";

export type ReportType =
  | "supplier-performance"
  | "stock-aging"
  | "stock-valuation"
  | "sales-summary"
  | "margin-analysis"
  | "pnl"
  | "balance-sheet"
  | "cash-flow"
  | "custom-builder";

export interface ReportPageProps {
  reportType: ReportType;
  title?: string;
}

interface Row { [k: string]: string | number | undefined; }

export function ReportPage({ reportType, title }: ReportPageProps) {
  const erp = useERP();
  const { reportTitle, subtitle, metaCards, columns, rows, totals } = React.useMemo(() => buildReport(reportType, erp), [reportType, erp]);

  const printData: PrintData = React.useMemo(() => ({
    title: title || reportTitle,
    docNumber: `RPT-${reportType.toUpperCase()}-${new Date().getFullYear()}`,
    docDate: new Date().toISOString().slice(0, 10),
    meta: metaCards.map(m => ({ label: m.label, value: String(m.value) })),
    columns: columns.map(c => ({ key: c.key, label: c.header, align: c.align })),
    rows: rows as Record<string, React.ReactNode | string | number>[],
    totals,
    signatures: [
      { label: "Prepared By", name: "System" },
      { label: "Reviewed By", name: "Finance Manager" },
      { label: "Approved By", name: "CEO" },
    ],
  }), [reportTitle, columns, rows, metaCards, totals, title, reportType]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FileBarChart className="text-primary size-5" />
            <h1 className="text-xl font-semibold sm:text-2xl">{title || reportTitle}</h1>
          </div>
          {subtitle && <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <PrintButton data={printData} label="Print / PDF" />
          <CSVExportButton columns={columns.map(c => ({ key: c.key, label: c.header }))} rows={rows} filename={`${reportType}-${Date.now()}.csv`} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metaCards.map((m, i) => (
          <Card key={i}>
            <CardHeader className="pb-2"><CardTitle className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide">
              {m.icon === "calendar" ? <Calendar className="size-3" /> : m.icon === "building" ? <Building2 className="size-3" /> : <Hash className="size-3" />}
              {m.label}
            </CardTitle></CardHeader>
            <CardContent><div className={`text-xl font-semibold ${m.tone || ""}`}>{m.value}</div></CardContent>
          </Card>
        ))}
      </div>

      {totals && totals.length > 0 && (
        <Card>
          <CardContent className="grid grid-cols-1 gap-3 pt-6 sm:grid-cols-3">
            {totals.map((t, i) => (
              <div key={i} className="rounded-md border bg-muted/30 p-3">
                <p className="text-muted-foreground text-xs">{t.label}</p>
                <p className="mt-1 text-lg font-semibold">{t.value}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Detail</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <DataTable
              columns={columns}
              data={rows as Row[]}
              rowKey={(r) => String(r.id ?? Math.random())}
              pageSize={15}
              maxHeight="55vh"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function buildReport(reportType: ReportType, erp: ERPState) {
  if (reportType === "supplier-performance") {
    const rows = erp.suppliers.map(s => {
      const pos = erp.purchaseOrders.filter(p => p.supplierId === s.id);
      const totalPO = pos.length;
      const totalValue = pos.reduce((sum, p) => sum + p.total, 0);
      const onTime = pos.filter(p => p.status === "Received").length;
      return {
        id: s.id, code: s.code, name: s.name, category: s.category,
        totalPO, totalValue, onTime, rating: s.rating, leadTimeDays: s.leadTimeDays,
        onTimeRate: totalPO > 0 ? `${Math.round((onTime / totalPO) * 100)}%` : "—",
      };
    });
    return {
      reportTitle: "Supplier Performance Report",
      subtitle: "OTIF (On-Time-In-Full) and lead-time analysis across all suppliers.",
      metaCards: [
        { label: "Suppliers", value: formatNum(erp.suppliers.length), icon: "building" },
        { label: "Total PO Value", value: formatBDT(rows.reduce((s, r) => s + (r.totalValue as number), 0)), icon: "calendar" },
        { label: "Avg Rating", value: `★ ${(erp.suppliers.reduce((s, x) => s + x.rating, 0) / Math.max(1, erp.suppliers.length)).toFixed(2)}`, icon: "hash" },
        { label: "Top Performer", value: erp.suppliers.slice().sort((a, b) => b.rating - a.rating)[0]?.name ?? "—", icon: "building" },
      ],
      columns: [
        { key: "code", header: "Code", sortable: true },
        { key: "name", header: "Supplier", sortable: true },
        { key: "category", header: "Category" },
        { key: "totalPO", header: "PO Count", align: "right", sortable: true },
        { key: "totalValue", header: "Total Value", align: "right", sortable: true, render: r => formatBDT(Number(r.totalValue)) },
        { key: "onTime", header: "On-Time", align: "right" },
        { key: "onTimeRate", header: "OTIF %", align: "right", render: r => <Badge variant={String(r.onTimeRate).startsWith("10") ? "default" : "secondary"}>{r.onTimeRate}</Badge> },
        { key: "leadTimeDays", header: "Lead Time", align: "right", render: r => `${r.leadTimeDays}d` },
        { key: "rating", header: "Rating", align: "right", render: r => `★ ${Number(r.rating).toFixed(1)}` },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total PO Count", value: formatNum(rows.reduce((s, r) => s + (r.totalPO as number), 0)) },
        { label: "Total PO Value", value: formatBDT(rows.reduce((s, r) => s + (r.totalValue as number), 0)) },
        { label: "Avg Lead Time", value: `${Math.round(rows.reduce((s, r) => s + (r.leadTimeDays as number), 0) / Math.max(1, rows.length))}d` },
      ],
    };
  }
  if (reportType === "stock-aging") {
    const rows = erp.products.map(p => {
      const stock = erp.getStock(p.id);
      const aged = Math.floor(Math.random() * 180);
      return {
        id: p.id, sku: p.sku, name: p.name, category: p.category,
        stock,
        value: stock * p.costPrice,
        agingDays: aged,
        bucket: aged > 120 ? "Stale" : aged > 60 ? "Aging" : "Fresh",
      };
    });
    return {
      reportTitle: "Stock Aging Report",
      subtitle: "Days-in-stock analysis to identify slow-moving inventory.",
      metaCards: [
        { label: "SKUs", value: formatNum(erp.products.length), icon: "hash" },
        { label: "Total Stock Value", value: formatBDT(rows.reduce((s, r) => s + (r.value as number), 0)), icon: "calendar" },
        { label: "Stale Items", value: formatNum(rows.filter(r => r.bucket === "Stale").length), tone: "text-destructive", icon: "hash" },
        { label: "Fresh Items", value: formatNum(rows.filter(r => r.bucket === "Fresh").length), tone: "text-primary", icon: "hash" },
      ],
      columns: [
        { key: "sku", header: "SKU", sortable: true },
        { key: "name", header: "Product", sortable: true },
        { key: "category", header: "Category" },
        { key: "stock", header: "Stock Qty", align: "right", sortable: true, render: r => formatNum(Number(r.stock)) },
        { key: "value", header: "Stock Value", align: "right", sortable: true, render: r => formatBDT(Number(r.value)) },
        { key: "agingDays", header: "Days in Stock", align: "right", sortable: true, render: r => `${r.agingDays}d` },
        { key: "bucket", header: "Bucket", render: r => <Badge variant={r.bucket === "Stale" ? "destructive" : r.bucket === "Aging" ? "secondary" : "default"}>{r.bucket}</Badge> },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total Stock Value", value: formatBDT(rows.reduce((s, r) => s + (r.value as number), 0)) },
        { label: "Stale Value", value: formatBDT(rows.filter(r => r.bucket === "Stale").reduce((s, r) => s + (r.value as number), 0)) },
        { label: "Avg Aging", value: `${Math.round(rows.reduce((s, r) => s + (r.agingDays as number), 0) / Math.max(1, rows.length))}d` },
      ],
    };
  }
  if (reportType === "stock-valuation") {
    const rows = erp.products.map(p => {
      const stock = erp.getStock(p.id);
      return {
        id: p.id, sku: p.sku, name: p.name, category: p.category,
        stock, costValue: stock * p.costPrice, saleValue: stock * p.salePrice,
        potentialMargin: stock * (p.salePrice - p.costPrice),
      };
    });
    return {
      reportTitle: "Stock Valuation Report",
      subtitle: "Inventory valuation at cost and potential sale value.",
      metaCards: [
        { label: "Total SKUs", value: formatNum(erp.products.length), icon: "hash" },
        { label: "Cost Value", value: formatBDT(rows.reduce((s, r) => s + (r.costValue as number), 0)), icon: "calendar" },
        { label: "Sale Value", value: formatBDT(rows.reduce((s, r) => s + (r.saleValue as number), 0)), icon: "calendar" },
        { label: "Potential Margin", value: formatBDT(rows.reduce((s, r) => s + (r.potentialMargin as number), 0)), tone: "text-primary", icon: "calendar" },
      ],
      columns: [
        { key: "sku", header: "SKU", sortable: true },
        { key: "name", header: "Product", sortable: true },
        { key: "category", header: "Category" },
        { key: "stock", header: "Qty", align: "right", render: r => formatNum(Number(r.stock)) },
        { key: "costValue", header: "Cost Value", align: "right", sortable: true, render: r => formatBDT(Number(r.costValue)) },
        { key: "saleValue", header: "Sale Value", align: "right", sortable: true, render: r => formatBDT(Number(r.saleValue)) },
        { key: "potentialMargin", header: "Potential Margin", align: "right", render: r => <span className="text-primary">{formatBDT(Number(r.potentialMargin))}</span> },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total Cost Value", value: formatBDT(rows.reduce((s, r) => s + (r.costValue as number), 0)) },
        { label: "Total Sale Value", value: formatBDT(rows.reduce((s, r) => s + (r.saleValue as number), 0)) },
        { label: "Total Potential Margin", value: formatBDT(rows.reduce((s, r) => s + (r.potentialMargin as number), 0)) },
      ],
    };
  }
  if (reportType === "sales-summary") {
    const rows = erp.salesInvoices.map(inv => {
      const cust = erp.getCustomer(inv.customerId);
      return {
        id: inv.id, invoiceNumber: inv.invoiceNumber, customer: cust?.name ?? "—",
        zone: cust?.zone ?? "—", amount: inv.amount, tax: inv.tax, total: inv.total,
        paid: inv.paidAmount, outstanding: inv.total - inv.paidAmount,
        status: inv.status,
      };
    });
    return {
      reportTitle: "Sales Summary Report",
      subtitle: "Invoice-level sales performance and outstanding analysis.",
      metaCards: [
        { label: "Total Invoices", value: formatNum(erp.salesInvoices.length), icon: "hash" },
        { label: "Gross Sales", value: formatBDT(erp.salesInvoices.reduce((s, i) => s + i.amount, 0)), icon: "calendar" },
        { label: "VAT Collected", value: formatBDT(erp.salesInvoices.reduce((s, i) => s + i.tax, 0)), icon: "calendar" },
        { label: "Outstanding", value: formatBDT(rows.reduce((s, r) => s + (r.outstanding as number), 0)), tone: "text-destructive", icon: "calendar" },
      ],
      columns: [
        { key: "invoiceNumber", header: "Invoice #", sortable: true },
        { key: "customer", header: "Customer", sortable: true },
        { key: "zone", header: "Zone" },
        { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(Number(r.amount)) },
        { key: "tax", header: "VAT", align: "right", render: r => formatBDT(Number(r.tax)) },
        { key: "total", header: "Total", align: "right", sortable: true, render: r => formatBDT(Number(r.total)) },
        { key: "paid", header: "Paid", align: "right", render: r => formatBDT(Number(r.paid)) },
        { key: "outstanding", header: "Outstanding", align: "right", render: r => <span className={Number(r.outstanding) > 0 ? "text-destructive font-medium" : ""}>{formatBDT(Number(r.outstanding))}</span> },
        { key: "status", header: "Status", render: r => <Badge variant={r.status === "Paid" ? "default" : r.status === "Posted" ? "secondary" : "destructive"}>{r.status}</Badge> },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Gross Sales", value: formatBDT(rows.reduce((s, r) => s + (r.amount as number), 0)) },
        { label: "Total Outstanding", value: formatBDT(rows.reduce((s, r) => s + (r.outstanding as number), 0)) },
        { label: "Total Paid", value: formatBDT(rows.reduce((s, r) => s + (r.paid as number), 0)) },
      ],
    };
  }
  if (reportType === "margin-analysis") {
    const rows = erp.salesOrders.map(so => {
      const cust = erp.getCustomer(so.customerId);
      return {
        id: so.id, soNumber: so.soNumber, customer: cust?.name ?? "—",
        total: so.total, margin: so.margin, marginValue: so.total * so.margin / 100,
        status: so.status,
      };
    });
    return {
      reportTitle: "Margin Analysis Report",
      subtitle: "Order-level profitability and margin tracking.",
      metaCards: [
        { label: "Total Orders", value: formatNum(erp.salesOrders.length), icon: "hash" },
        { label: "Net Sales", value: formatBDT(erp.salesOrders.reduce((s, o) => s + o.total, 0)), icon: "calendar" },
        { label: "Avg Margin %", value: `${(erp.salesOrders.reduce((s, o) => s + o.margin, 0) / Math.max(1, erp.salesOrders.length)).toFixed(1)}%`, icon: "calendar" },
        { label: "Low Margin Orders", value: formatNum(erp.salesOrders.filter(o => o.margin < 15).length), tone: "text-destructive", icon: "hash" },
      ],
      columns: [
        { key: "soNumber", header: "SO #", sortable: true },
        { key: "customer", header: "Customer", sortable: true },
        { key: "total", header: "Order Value", align: "right", sortable: true, render: r => formatBDT(Number(r.total)) },
        { key: "margin", header: "Margin %", align: "right", sortable: true, render: r => <span className={Number(r.margin) < 15 ? "text-destructive font-semibold" : "text-primary"}>{Number(r.margin).toFixed(1)}%</span> },
        { key: "marginValue", header: "Margin Value", align: "right", render: r => formatBDT(Number(r.marginValue)) },
        { key: "status", header: "Status", render: r => <Badge variant="secondary">{r.status}</Badge> },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total Sales", value: formatBDT(rows.reduce((s, r) => s + (r.total as number), 0)) },
        { label: "Total Margin", value: formatBDT(rows.reduce((s, r) => s + (r.marginValue as number), 0)) },
        { label: "Avg Margin %", value: `${(rows.reduce((s, r) => s + (r.margin as number), 0) / Math.max(1, rows.length)).toFixed(1)}%` },
      ],
    };
  }
  if (reportType === "pnl") {
    const revenue = erp.salesInvoices.reduce((s, i) => s + i.amount, 0);
    const cogs = erp.journalEntries.filter(j => j.description.includes("Sales Issue")).reduce((s, j) => s + (j.lines.find(l => l.account === "Cost of Goods Sold")?.debit ?? 0), 0);
    const grossProfit = revenue - cogs;
    const opex = 480000;
    const netProfit = grossProfit - opex;
    const rows: Row[] = [
      { id: "r1", account: "Sales Revenue", debit: 0, credit: revenue, type: "Income" },
      { id: "r2", account: "Cost of Goods Sold", debit: cogs, credit: 0, type: "COGS" },
      { id: "r3", account: "Gross Profit", debit: 0, credit: grossProfit, type: "Subtotal" },
      { id: "r4", account: "Operating Expenses", debit: opex, credit: 0, type: "OpEx" },
      { id: "r5", account: "Net Profit", debit: 0, credit: netProfit, type: "Net" },
    ];
    return {
      reportTitle: "Profit & Loss Statement",
      subtitle: "Income statement for current period.",
      metaCards: [
        { label: "Revenue", value: formatBDT(revenue), tone: "text-primary", icon: "calendar" },
        { label: "COGS", value: formatBDT(cogs), tone: "text-destructive", icon: "calendar" },
        { label: "Gross Profit", value: formatBDT(grossProfit), icon: "calendar" },
        { label: "Net Profit", value: formatBDT(netProfit), tone: netProfit >= 0 ? "text-primary" : "text-destructive", icon: "calendar" },
      ],
      columns: [
        { key: "account", header: "Account", sortable: true },
        { key: "type", header: "Type", render: r => <Badge variant={r.type === "Net" || r.type === "Subtotal" ? "default" : "secondary"}>{r.type}</Badge> },
        { key: "debit", header: "Debit", align: "right", render: r => r.debit ? formatBDT(Number(r.debit)) : "—" },
        { key: "credit", header: "Credit", align: "right", render: r => r.credit ? formatBDT(Number(r.credit)) : "—" },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total Revenue", value: formatBDT(revenue) },
        { label: "Total COGS", value: formatBDT(cogs) },
        { label: "Net Profit", value: formatBDT(netProfit) },
      ],
    };
  }
  if (reportType === "balance-sheet") {
    const totalAssets = erp.products.reduce((s, p) => s + p.costPrice * erp.getStock(p.id), 0);
    const totalAR = erp.customers.reduce((s, c) => s + c.outstanding, 0);
    const totalAP = erp.purchaseOrders.filter(p => p.status === "Received" || p.status === "Partially Received").reduce((s, p) => s + p.total, 0);
    const cash = 5800000;
    const rows: Row[] = [
      { id: "as1", account: "Cash & Bank", debit: cash, credit: 0, type: "Asset" },
      { id: "as2", account: "Accounts Receivable", debit: totalAR, credit: 0, type: "Asset" },
      { id: "as3", account: "Inventory", debit: totalAssets, credit: 0, type: "Asset" },
      { id: "li1", account: "Accounts Payable", debit: 0, credit: totalAP, type: "Liability" },
      { id: "eq1", account: "Owner's Equity", debit: 0, credit: cash + totalAR + totalAssets - totalAP, type: "Equity" },
    ];
    return {
      reportTitle: "Balance Sheet",
      subtitle: "Statement of financial position.",
      metaCards: [
        { label: "Total Assets", value: formatBDT(cash + totalAR + totalAssets), icon: "calendar" },
        { label: "Total Liabilities", value: formatBDT(totalAP), icon: "calendar" },
        { label: "Inventory Value", value: formatBDT(totalAssets), icon: "calendar" },
        { label: "AR Outstanding", value: formatBDT(totalAR), tone: "text-destructive", icon: "calendar" },
      ],
      columns: [
        { key: "account", header: "Account", sortable: true },
        { key: "type", header: "Type", render: r => <Badge variant={r.type === "Asset" ? "default" : r.type === "Liability" ? "destructive" : "secondary"}>{r.type}</Badge> },
        { key: "debit", header: "Debit", align: "right", render: r => r.debit ? formatBDT(Number(r.debit)) : "—" },
        { key: "credit", header: "Credit", align: "right", render: r => r.credit ? formatBDT(Number(r.credit)) : "—" },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total Assets", value: formatBDT(cash + totalAR + totalAssets) },
        { label: "Total Liabilities", value: formatBDT(totalAP) },
        { label: "Equity", value: formatBDT(cash + totalAR + totalAssets - totalAP) },
      ],
    };
  }
  if (reportType === "cash-flow") {
    const rows: Row[] = [
      { id: "op1", account: "Cash from Customers", debit: erp.customerReceipts.reduce((s, r) => s + r.amount, 0), credit: 0, type: "Operating" },
      { id: "op2", account: "Cash to Suppliers", debit: 0, credit: 850000, type: "Operating" },
      { id: "op3", account: "Operating Net", debit: erp.customerReceipts.reduce((s, r) => s + r.amount, 0) - 850000, credit: 0, type: "Subtotal" },
      { id: "iv1", account: "CapEx (Fleet)", debit: 0, credit: 1200000, type: "Investing" },
      { id: "fn1", account: "Loan Repayment", debit: 0, credit: 600000, type: "Financing" },
    ];
    const netCash = rows.reduce((s, r) => s + (Number(r.debit) - Number(r.credit)), 0);
    return {
      reportTitle: "Cash Flow Statement",
      subtitle: "Operating, Investing, and Financing cash flows.",
      metaCards: [
        { label: "Cash Inflow", value: formatBDT(rows.reduce((s, r) => s + Number(r.debit), 0)), tone: "text-primary", icon: "calendar" },
        { label: "Cash Outflow", value: formatBDT(rows.reduce((s, r) => s + Number(r.credit), 0)), tone: "text-destructive", icon: "calendar" },
        { label: "Net Cash", value: formatBDT(netCash), tone: netCash >= 0 ? "text-primary" : "text-destructive", icon: "calendar" },
        { label: "Period", value: new Date().getFullYear().toString(), icon: "calendar" },
      ],
      columns: [
        { key: "account", header: "Activity", sortable: true },
        { key: "type", header: "Type", render: r => <Badge variant="secondary">{r.type}</Badge> },
        { key: "debit", header: "Inflow", align: "right", render: r => r.debit ? formatBDT(Number(r.debit)) : "—" },
        { key: "credit", header: "Outflow", align: "right", render: r => r.credit ? formatBDT(Number(r.credit)) : "—" },
      ] as Column<Row>[],
      rows,
      totals: [
        { label: "Total Inflow", value: formatBDT(rows.reduce((s, r) => s + Number(r.debit), 0)) },
        { label: "Total Outflow", value: formatBDT(rows.reduce((s, r) => s + Number(r.credit), 0)) },
        { label: "Net Cash", value: formatBDT(netCash) },
      ],
    };
  }
  // custom-builder fallback
  const rows: Row[] = erp.journalEntries.map(j => ({
    id: j.id, jeNumber: j.jeNumber, date: j.date, description: j.description,
    debit: j.totalDebit, credit: j.totalCredit, status: j.status,
  }));
  return {
    reportTitle: "Custom Report Builder",
    subtitle: "Build ad-hoc reports from any entity.",
    metaCards: [
      { label: "Records", value: formatNum(rows.length), icon: "hash" },
      { label: "Total Debit", value: formatBDT(rows.reduce((s, r) => s + Number(r.debit), 0)), icon: "calendar" },
      { label: "Total Credit", value: formatBDT(rows.reduce((s, r) => s + Number(r.credit), 0)), icon: "calendar" },
      { label: "Period", value: new Date().getFullYear().toString(), icon: "calendar" },
    ],
    columns: [
      { key: "jeNumber", header: "JE #", sortable: true },
      { key: "date", header: "Date", sortable: true },
      { key: "description", header: "Description" },
      { key: "debit", header: "Debit", align: "right", sortable: true, render: r => formatBDT(Number(r.debit)) },
      { key: "credit", header: "Credit", align: "right", sortable: true, render: r => formatBDT(Number(r.credit)) },
      { key: "status", header: "Status", render: r => <Badge variant="secondary">{r.status}</Badge> },
    ] as Column<Row>[],
    rows,
    totals: [
      { label: "Total Debit", value: formatBDT(rows.reduce((s, r) => s + Number(r.debit), 0)) },
      { label: "Total Credit", value: formatBDT(rows.reduce((s, r) => s + Number(r.credit), 0)) },
      { label: "Balanced", value: rows.reduce((s, r) => s + Number(r.debit), 0) === rows.reduce((s, r) => s + Number(r.credit), 0) ? "Yes" : "No" },
    ],
  };
}
